import { Viewer } from "../main";
import {
  AmbientLight,
  BackSide,
  BoxBufferGeometry,
  DoubleSide,
  FrontSide,
  Group,
  Mesh,
  MeshPhongMaterial,
  PerspectiveCamera,
  PlaneBufferGeometry,
  Raycaster,
  Scene,
  SpotLight,
  Texture,
  Vector2,
  Vector3,
  WebGLRenderer
} from "three";
import { eventToPixel } from "../utilities";
import { SceneCoordinate } from "../projections";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass";
import { CopyShader } from "three/examples/jsm/shaders/CopyShader";
import { SSAARenderPass } from "three/examples/jsm/postprocessing/SSAARenderPass";
import { CustomMouseEvent } from ".";
import { clearViewerTooltip } from "../../viewer";
import {t} from "../../../localization";

class TextMaterial extends MeshPhongMaterial {
  public textures;
  public text: string;
  public key: string;

  constructor(opts, textures, text, key) {
    super(opts);

    this.textures = textures;
    this.text = text;
    this.key = key;
  }
}

export class CompassControls {
  private viewer: Viewer;
  private scene: Scene;
  private renderer: WebGLRenderer;
  private composer: EffectComposer
  private camera: PerspectiveCamera;
  private raycaster = new Raycaster();
  private pixelRatio = 2;
  private antialias = true;

  private sizeRatioLarge = 0.20;
  private sizeRatioSmall = 0.40;
  private alphaTest = 0.25;
  private lightPosition = new Vector3(3, 3, 3);
  private pivot: Group;

  private cube: Mesh;
  private cubeSize = 0.4;
  private cubeColorStandard = "#dddddd";
  private cubeColorHover = "#aaaaaa";
  private cubeEdgeColor = "#444444";
  private cubeTextColor = "#000000";
  private cubeMaterials: TextMaterial[] = [];

  private donutSize = 0.80;
  private donutColor = "#444444";
  private donutTextColor = "#ffffff";

  private north: Group;
  private west: Group;
  private east: Group;
  private south: Group;

  constructor(viewer: Viewer, props) {
    const {compassClassName} = props;

    this.viewer = viewer;

    this.initScene();
    this.initAntiAliasing();
    this.addObjects();
    this.addLights();

    // Apply compass class name from props
    this.domElement.className = compassClassName;
  }

  get width() {
    return this.domElement.width;
  }

  get height() {
    return this.domElement.height;
  }

  get domElement() {
    return this.renderer.domElement;
  }

  get cameraDistance() {
    return this.donutSize * 2.0;
  }

  get letters() {
    return [this.east, this.west, this.north, this.south];
  }

  initScene() {
    this.renderer = new WebGLRenderer({
      alpha: true,
    });

    this.renderer.setPixelRatio(this.pixelRatio);
    this.renderer.domElement.id = "compass-canvas";

    this.camera = new PerspectiveCamera(40, 1.0, 1, 10);
    this.camera.position.z = this.cameraDistance;
    this.camera.up = new Vector3(0, 1, 0);
    this.camera.lookAt(new Vector3(0, 0, 0));
    this.scene = new Scene();
  }

  initAntiAliasing() {
    this.composer = new EffectComposer(this.renderer);

    let ssaaRenderPass = new SSAARenderPass(this.scene,
      this.camera, "white", 0.0);

    ssaaRenderPass.unbiased = true;
    ssaaRenderPass.sampleLevel = 3;
    this.composer.addPass(ssaaRenderPass);

    let copyPass = new ShaderPass(CopyShader);
    this.composer.addPass(copyPass);
  }

  addObjects() {
    this.pivot = new Group();
    this.pivot.position.set(0, 0, 0);
    this.pivot.visible = false;

    this.addRotationCube();
    this.addCompassRing();
    this.addCompassLetters();

    // Add all items to scene
    this.scene.add(this.pivot);
  }

  addLights() {
    let ambientLight = new AmbientLight(0xffffff, 0.9);
    this.scene.add(ambientLight);

    let spotLight = new SpotLight(0xffffff, 0.5);
    spotLight.castShadow = true;
    spotLight.position.copy(this.lightPosition);
    this.scene.add(spotLight);
  }

  addRotationCube() {
    const cubeGeometry = new BoxBufferGeometry(this.cubeSize,
      this.cubeSize, this.cubeSize);

    this.cubeMaterials = [
      this.getTextMaterial("right", t("directions.right"), 270),
      this.getTextMaterial("left", t("directions.left"), 90),
      this.getTextMaterial("back", t("directions.back"), 180),
      this.getTextMaterial("front", t("directions.front"), 0),
      this.getTextMaterial("top", t("directions.top"), 0),
      this.getTextMaterial("bottom", t("directions.bottom"), 180),
    ];

    this.pivot.remove(this.cube);
    this.cube = new Mesh(cubeGeometry, this.cubeMaterials);
    this.pivot.add(this.cube);
  }

  addCompassRing() {
    let donutGeometry = new PlaneBufferGeometry(
      this.donutSize, this.donutSize
    );

    let donutMaterial = this.getCircleMaterial();
    donutMaterial.alphaTest = this.alphaTest;
    donutMaterial.side = DoubleSide;

    const mesh = new Mesh(donutGeometry, donutMaterial);
    mesh.position.z = -this.cubeSize / 2.0;
    this.pivot.add(mesh);
  }

  addCompassLetters() {
    this.letters.forEach(letter => this.pivot.remove(letter));
    this.east = this.getDirectionMesh("east", t("directions.shorthand-east"));
    this.west = this.getDirectionMesh("west", t("directions.shorthand-west"));
    this.north = this.getDirectionMesh("north", t("directions.shorthand-north"));
    this.south = this.getDirectionMesh("south", t("directions.shorthand-south"));
    this.letters.forEach(letter => this.pivot.add(letter));
  }

  getTextMaterial(direction, text, rotation): TextMaterial {
    let canvasSize = 1024;
    let fontSize = 200;
    let border = 32;
    let outlineSize = 5;

    // Scale is applied to fit text inside canvas
    let maxLength = canvasSize / fontSize;
    let fontScale = Math.min(1.0, maxLength / text.length);
    fontSize = fontSize * fontScale;

    let textures = [];

    [this.cubeColorStandard, this.cubeColorHover].forEach(cubeColor => {
      let canvas = document.createElement("canvas");
      canvas.width  = canvasSize;
      canvas.height = canvasSize;
      let context = canvas.getContext("2d");

      // Rotate from center point
      context.translate( canvasSize / 2, canvasSize / 2 );
      context.rotate(rotation * Math.PI / 180);
      context.translate( -canvasSize / 2, -canvasSize / 2 );

      // Fill background (border + color)
      context.fillStyle = this.cubeEdgeColor;
      context.fillRect(0, 0, canvasSize, canvasSize);
      context.fillStyle = cubeColor;
      context.fillRect(border, border, canvasSize - 2 * border,
        canvasSize - 2 * border);

      // Write text
      let x = canvasSize / 2.0;
      let y = canvasSize / 2.0 + fontSize / 2.0;
      context.textAlign = "center";
      context.font = `bold ${fontSize}px Verdana`;
      context.fillStyle = this.cubeTextColor;
      context.fillText(text, x, y);

      // Black outline
      context.lineWidth = outlineSize;
      context.strokeStyle = "#000000";
      context.strokeText(text, x, y);

      let texture = new Texture(canvas);
      texture.needsUpdate = true;
      textures.push(texture);
    });

    let material = new TextMaterial({
      map: textures[0],
    }, textures, text, direction);

    return material;
  }

  getLetterMaterial(text): MeshPhongMaterial {
    let canvasSize = 256;
    let fontSize = 230;
    let outlineSize = 5;

    let canvas = document.createElement("canvas");
    canvas.width  = canvasSize;
    canvas.height = canvasSize;
    let context = canvas.getContext("2d");

    let x = canvasSize / 2.0;
    let y = canvasSize / 2.0 + fontSize / 2.0 - fontSize * 0.1;
    context.textAlign = "center";
    context.font = `bold ${fontSize}px Verdana`;

    // Colored text
    context.fillStyle = this.donutTextColor;
    context.fillText(text, x, y);

    // Black outline
    context.lineWidth = outlineSize;
    context.strokeStyle = "#000000";
    context.strokeText(text, x, y);

    let texture = new Texture(canvas);
    texture.needsUpdate = true;

    return new MeshPhongMaterial({
      map: texture
    });
  }

  getCircleMaterial(): MeshPhongMaterial {
    let canvasSize = 256;

    let canvas = document.createElement("canvas");
    canvas.width  = canvasSize;
    canvas.height = canvasSize;
    let context = canvas.getContext("2d");

    context.fillStyle = this.donutColor;

    let x = canvasSize / 2.0;
    let y = canvasSize / 2.0;

    context.beginPath();
    context.arc(x, y, canvasSize * 0.5 * 1.00, 0, Math.PI * 2, false);
    context.arc(x, y, canvasSize * 0.5 * 0.75, 0, Math.PI * 2, true);
    context.fill();

    let texture = new Texture(canvas);
    texture.needsUpdate = true;

    return new MeshPhongMaterial({
      map: texture
    });
  }

  getDirectionMesh(direction, text): Group {
    let pivot = new Group();

    // Set the height just barely above the circle texture
    let textHeight = this.cubeSize * 0.005;

    if (direction === "east") {
      pivot.position.x = this.cubeSize;
    } else if (direction === "west") {
      pivot.position.x = -1 * this.cubeSize;
    } else if (direction === "north") {
      pivot.position.y = this.cubeSize;
    } else if (direction === "south") {
      pivot.position.y = -1 * this.cubeSize;
    }

    pivot.position.z = -this.cubeSize / 2.0;

    let geometryUpper = new PlaneBufferGeometry(this.donutSize * 0.25,
      this.donutSize * 0.25);
    let materialUpper = this.getLetterMaterial(text);
    materialUpper.alphaTest = this.alphaTest;
    materialUpper.side = FrontSide;
    let meshUpper = new Mesh(geometryUpper, materialUpper);
    meshUpper.position.z =  textHeight;
    pivot.add(meshUpper);

    let geometryLower = new PlaneBufferGeometry(this.donutSize * 0.25,
      this.donutSize * 0.25);
    let materialLower = this.getLetterMaterial(text);
    materialLower.alphaTest = this.alphaTest;
    materialLower.side = BackSide;
    let meshLower = new Mesh(geometryLower, materialLower);
    meshLower.scale.y = -1;
    meshLower.position.z = -1 * textHeight;
    pivot.add(meshLower);

    return pivot;
  }

  onMouseMove(event) {
    let {hovering} = this.checkMouseHover(event);

    return hovering;
  }

  onMouseUp(event: CustomMouseEvent): boolean {
    if (event.mouseMoved) {
      return false;
    }

    let controls = this.viewer.controls;
    let {hovering, side} = this.checkMouseHover(event);

    if (side) {
      controls.resetCamera(side);
    }

    return hovering;
  }

  checkMouseHover(event) {
    let side = this.getSelectedSide(event);
    let hovering = side ? true : false;
    this.togglePointer(hovering);

    return {hovering, side};
  }

  getSelectedSide(event) {
    if (!this.cube.visible) {
      return;
    }

    let offset = new Vector3(
      this.domElement.offsetLeft,
      this.domElement.offsetTop,
      0
    );

    let {x, y} = eventToPixel(event).sub(offset);

    let mouse = new Vector2();
    mouse.x = (x / this.width) * this.pixelRatio * 2 - 1;
    mouse.y = -1 * (y / this.height) * this.pixelRatio * 2 + 1;

    if ((mouse.x < -1) || (mouse.x > 1)) {
      return;
    }

    if ((mouse.y < -1) || (mouse.y > 1)) {
      return;
    }

    this.raycaster.setFromCamera(mouse, this.camera);

    let intersect = this.raycaster
      .intersectObject(this.cube);

    if (intersect.length === 0) {
      this.setCubeMaterials();
      return;
    }

    let materialIndex = Math.floor(intersect[0].faceIndex / 2);
    let material = this.cubeMaterials[materialIndex];
    this.setCubeMaterials(materialIndex);

    return material.key;
  }

  setCubeMaterials(hoverIndex = null) {
    let materials = this.cube.material as TextMaterial[];

    for (var i = 0; i < materials.length; i++) {
      if (hoverIndex === i) {
        this.cube.material[i].map = materials[i].textures[1];
      } else {
        this.cube.material[i].map = materials[i].textures[0];
      }

      this.cube.material[i].needsUpdate = true;
    }
  }

  rotateLetters(yaw) {
    this.east.rotation.z = yaw;
    this.west.rotation.z = yaw;
    this.north.rotation.z = yaw;
    this.south.rotation.z = yaw;
  }

  zenith(pointA, pointB) {
    let height = pointA.z - pointB.z;
    let distance = pointA.distanceTo(pointB);
    let zenith = Math.acos(height / distance);
    return -1 * zenith;
  }

  bearing(pointA, pointB) {
    let startLat = pointA.y * Math.PI / 180;
    let destLat = pointB.y * Math.PI / 180;

    let startLng = pointA.x * Math.PI / 180;
    let destLng = pointB.x * Math.PI / 180;

    let y = Math.sin(destLng - startLng) * Math.cos(destLat);
    let x = Math.cos(startLat) * Math.sin(destLat) -
              Math.sin(startLat) * Math.cos(destLat) * Math.cos(destLng - startLng);
    let  brng = Math.atan2(y, x);
    brng = brng * 180 / Math.PI;
    brng = (brng + 360.0) % 360;
    return brng * Math.PI / 180;
  }

  updateOffset(offset) {
    let shift = this.viewer.expanded
      ? offset
      : 0;

    this.domElement.style.right = `${shift}px`;
  }

  setSize(width, height) {
    const ratio =  this.viewer.expanded
      ? this.sizeRatioLarge
      : this.sizeRatioSmall;

    const size = Math.min(width, height) * ratio;
    this.renderer.setSize(size, size);
    this.composer.setSize(size, size);
  }

  togglePointer(state) {
    if (state) {
      clearViewerTooltip();
    }

    const cursor = state ? "pointer" : "";
    this.viewer.setContainerCursor(cursor);
  }

  calculateRotations(loaded, camera, orbitMode) {
    if (!loaded) {
      return;
    }

    let quaternion = camera.quaternion;
    this.cube.visible = orbitMode;

    let lookatLocal = new Vector3(0, 0, -1)
      .applyQuaternion(quaternion)
      .add(camera.position);

    let lookatLonLat = new SceneCoordinate(lookatLocal)
      .toLonLat();

    let yPosLocal = new Vector3(0, 1, 0)
      .add(camera.position);

    let yPosLonLat = new SceneCoordinate(yPosLocal)
      .toLonLat();

    // Center point
    let cameraLocal = camera.position.clone();
    let cameraLatLon = new SceneCoordinate(cameraLocal)
      .toLonLat();

    let groupYaw = this.bearing(cameraLatLon, lookatLonLat);
    let groupPitch = orbitMode ? this.zenith(cameraLocal, lookatLocal) : 0;

    // Rotate our direction textures
    this.rotateLetters(-1 * groupYaw);

    // Pivot our geometry group
    this.pivot.rotation.z = groupYaw;
    this.pivot.rotation.x = groupPitch;

    // North wont equal our y-axis, so the cube is rotated independantly
    let cubeYaw = this.bearing(cameraLatLon, yPosLonLat);
    this.cube.rotation.z = -1 * cubeYaw;
  }

  render(loaded, camera, orbitMode) {
    this.calculateRotations(loaded, camera, orbitMode);
    this.pivot.visible = loaded;

    if (this.antialias) {
      this.composer.render();
    } else {
      this.renderer.render(this.scene, this.camera);
    }
  }
}
